import { applyDottedPatch } from './cloudSync.js';

// Goals with a checklist. The goal shell (title, notes, date, deleted) and each
// checklist row are separate Firebase fields. Writing the whole goal would let
// one device's title edit replace another device's step checks.
// Shell updatedAt moves only when the title, notes, target date, or deleted
// flag changes. Each step has its own updatedAt, so two devices can check
// different steps and both checks survive the merge.

const ID_RE = /^[A-Za-z0-9_-]{1,80}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SHELL_KEYS = ['id', 'title', 'notes', 'targetDate', 'updatedAt', 'deleted'];

const asMap = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : null
);

const clip = (value, max) => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text.slice(0, max);
};

const stampOf = (entry) => (typeof entry?.updatedAt === 'string' ? entry.updatedAt : '');

const validId = (value) => (typeof value === 'string' && ID_RE.test(value) ? value : '');

const pickId = (fallbackId, entryId) => {
  const fromKey = typeof fallbackId === 'string' ? fallbackId : String(fallbackId || '');
  return validId(fromKey) || validId(entryId);
};

const stampValue = (value) => (typeof value === 'string' && value ? value : null);

export const newGoalId = () => {
  const rand = Math.random().toString(36).slice(2, 8);
  return `g${Date.now().toString(36)}${rand}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 80);
};

export const newChecklistItemId = () => {
  const rand = Math.random().toString(36).slice(2, 8);
  return `c${Date.now().toString(36)}${rand}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 80);
};

export const normalizeChecklistItem = (entry, fallbackId) => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const id = pickId(fallbackId, entry.id);
  if (!id) return null;
  const text = clip(entry.text, 300);
  const deleted = entry.deleted === true;
  if (!deleted && !text) return null;
  const updatedAt = stampValue(entry.updatedAt);
  const createdAt = stampValue(entry.createdAt) || updatedAt;
  return {
    id,
    text,
    done: entry.done === true,
    createdAt,
    updatedAt,
    deleted
  };
};

const normalizeItems = (input) => {
  const out = {};
  const absorb = (entry, key) => {
    const norm = normalizeChecklistItem(entry, key ?? entry?.id);
    if (norm) out[norm.id] = norm;
  };
  if (Array.isArray(input)) {
    input.forEach((entry) => absorb(entry, entry?.id));
    return out;
  }
  const map = asMap(input);
  if (!map) return out;
  Object.entries(map).forEach(([key, entry]) => absorb(entry, key));
  return out;
};

export const normalizeGoal = (entry, fallbackId) => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const id = pickId(fallbackId, entry.id);
  if (!id) return null;
  const title = clip(entry.title, 200);
  const deleted = entry.deleted === true;
  if (!deleted && !title) return null;
  return {
    id,
    title,
    notes: clip(entry.notes, 2000),
    targetDate: DATE_RE.test(entry.targetDate) ? entry.targetDate : '',
    updatedAt: stampValue(entry.updatedAt),
    deleted,
    items: normalizeItems(entry.items)
  };
};

export const normalizeGoals = (input) => {
  const out = {};
  if (Array.isArray(input)) {
    input.forEach((entry) => {
      const norm = normalizeGoal(entry, entry?.id);
      if (norm) out[norm.id] = norm;
    });
    return out;
  }
  const map = asMap(input);
  if (!map) return out;
  Object.entries(map).forEach(([key, entry]) => {
    const norm = normalizeGoal(entry, key);
    if (norm) out[norm.id] = norm;
  });
  return out;
};

const mergeItems = (localItems, remoteItems) => {
  const out = {};
  const ids = new Set([...Object.keys(remoteItems || {}), ...Object.keys(localItems || {})]);
  ids.forEach((id) => {
    const loc = localItems?.[id];
    const rem = remoteItems?.[id];
    if (!loc) out[id] = rem;
    else if (!rem || stampOf(loc) >= stampOf(rem)) out[id] = loc;
    else out[id] = rem;
  });
  return out;
};

// Shell follows the newer goal updatedAt. Steps merge on their own stamps,
// including a delete, so a title edit cannot resurrect or drop a step.
export const mergeGoals = (local, remote) => {
  const localMap = normalizeGoals(local);
  const remoteMap = normalizeGoals(remote);
  const out = {};
  const ids = new Set([...Object.keys(remoteMap), ...Object.keys(localMap)]);
  ids.forEach((id) => {
    const loc = localMap[id];
    const rem = remoteMap[id];
    if (!loc) {
      out[id] = rem;
      return;
    }
    if (!rem) {
      out[id] = loc;
      return;
    }
    const shell = stampOf(loc) >= stampOf(rem) ? loc : rem;
    out[id] = { ...shell, id, items: mergeItems(loc.items, rem.items) };
  });
  return out;
};

const sameItem = (a, b) => !!a && !!b
  && a.id === b.id
  && a.text === b.text
  && a.done === b.done
  && a.deleted === b.deleted
  && (a.createdAt || null) === (b.createdAt || null)
  && (a.updatedAt || null) === (b.updatedAt || null);

// Dotted paths only. A whole-goal write would replace the other device's steps.
export const goalCloudPatch = (cloud, merged) => {
  const cloudMap = normalizeGoals(cloud);
  const next = normalizeGoals(merged);
  const patch = {};
  Object.entries(next).forEach(([id, goal]) => {
    const prev = cloudMap[id];
    SHELL_KEYS.forEach((key) => {
      const prevVal = prev ? prev[key] : undefined;
      if (!prev || prevVal !== goal[key]) patch[`goals.${id}.${key}`] = goal[key] ?? null;
    });
    const prevItems = prev?.items || {};
    Object.entries(goal.items || {}).forEach(([itemId, item]) => {
      if (!sameItem(prevItems[itemId], item)) patch[`goals.${id}.items.${itemId}`] = item;
    });
  });
  return patch;
};

export const goalShellCloudPatch = (goal) => {
  const patch = {};
  SHELL_KEYS.forEach((key) => {
    patch[`goals.${goal.id}.${key}`] = goal[key] ?? null;
  });
  return patch;
};

export const goalItemCloudPatch = (goalId, item) => ({
  [`goals.${goalId}.items.${item.id}`]: item
});

// Patches still waiting to reach Firebase. Laid onto the goals this device
// already has, in queue order, so a reload shows the edit before the server
// echo. An older queued field does not beat a newer local one: a step check
// that was saved and then removed stays removed.
export const applyGoalPatches = (goals, patches) => {
  const base = normalizeGoals(goals);
  let doc = { goals: base };
  (Array.isArray(patches) ? patches : []).forEach((patch) => {
    const own = {};
    Object.entries(patch || {}).forEach(([key, value]) => {
      if (key === 'goals' || key.startsWith('goals.')) own[key] = value;
    });
    if (Object.keys(own).length) doc = applyDottedPatch(doc, own);
  });
  return mergeGoals(base, doc.goals);
};

export const goalCreateCloudPatch = (goal) => {
  const patch = goalShellCloudPatch(goal);
  Object.values(goal.items || {}).forEach((item) => {
    patch[`goals.${goal.id}.items.${item.id}`] = item;
  });
  return patch;
};

export const checklistItemFromText = (text, { id, now = new Date().toISOString(), done = false } = {}) => {
  const itemId = validId(id) || newChecklistItemId();
  return normalizeChecklistItem({
    id: itemId,
    text,
    done,
    createdAt: now,
    updatedAt: now,
    deleted: false
  }, itemId);
};

export const goalFromDraft = (draft, { id, now = new Date().toISOString() } = {}) => {
  const goalId = validId(id) || validId(draft?.id) || newGoalId();
  const items = {};
  const rawItems = Array.isArray(draft?.items) ? draft.items : [];
  rawItems.forEach((raw) => {
    const item = checklistItemFromText(raw?.text, {
      id: raw?.id,
      now: stampValue(raw?.updatedAt) || now,
      done: raw?.done === true
    });
    if (item) items[item.id] = { ...item, createdAt: stampValue(raw?.createdAt) || item.createdAt };
  });
  const goal = normalizeGoal({
    id: goalId,
    title: draft?.title,
    notes: draft?.notes,
    targetDate: draft?.targetDate,
    updatedAt: now,
    deleted: false,
    items
  }, goalId);
  if (!goal?.title) return null;
  return goal;
};

// Title, notes, and target date only. Steps stay on the goal already stored.
export const updateGoalShell = (current, draft, now = new Date().toISOString()) => {
  if (!current || current.deleted) return null;
  const title = clip(draft?.title, 200);
  if (!title) return null;
  const notes = clip(draft?.notes, 2000);
  const targetDate = DATE_RE.test(draft?.targetDate) ? draft.targetDate : '';
  if (title === current.title && notes === current.notes && targetDate === (current.targetDate || '')) {
    return current;
  }
  return normalizeGoal({
    id: current.id,
    title,
    notes,
    targetDate,
    updatedAt: now,
    deleted: false,
    items: current.items
  }, current.id);
};

const replaceItem = (goal, item) => ({
  ...goal,
  items: { ...(goal.items || {}), [item.id]: item }
});

export const toggleChecklistItem = (goal, itemId, done, now = new Date().toISOString()) => {
  const item = goal?.items?.[itemId];
  if (!goal || goal.deleted || !item || item.deleted) return null;
  const next = normalizeChecklistItem({ ...item, done: !!done, updatedAt: now }, itemId);
  if (!next) return null;
  return replaceItem(goal, next);
};

export const addChecklistItem = (goal, text, now = new Date().toISOString()) => {
  if (!goal || goal.deleted) return null;
  const item = checklistItemFromText(text, { now });
  if (!item) return null;
  return { goal: replaceItem(goal, item), item };
};

export const deleteChecklistItem = (goal, itemId, now = new Date().toISOString()) => {
  const item = goal?.items?.[itemId];
  if (!goal || !item || item.deleted) return null;
  const tombstone = normalizeChecklistItem({ ...item, deleted: true, updatedAt: now }, itemId);
  if (!tombstone) return null;
  return { goal: replaceItem(goal, tombstone), item: tombstone };
};

export const restoreChecklistItem = (goal, item, now = new Date().toISOString()) => {
  if (!goal || !item?.id) return null;
  const revived = normalizeChecklistItem({ ...item, deleted: false, updatedAt: now }, item.id);
  if (!revived) return null;
  return { goal: replaceItem(goal, revived), item: revived };
};

export const deleteGoalShell = (goal, now = new Date().toISOString()) => {
  if (!goal || goal.deleted) return null;
  return normalizeGoal({ ...goal, deleted: true, updatedAt: now }, goal.id);
};

export const restoreGoalShell = (goal, now = new Date().toISOString()) => {
  if (!goal) return null;
  return normalizeGoal({ ...goal, deleted: false, updatedAt: now }, goal.id);
};

export const liveChecklist = (goal) => Object.values(goal?.items || {})
  .filter((item) => item && !item.deleted)
  .sort((a, b) => {
    const byCreated = (a.createdAt || '').localeCompare(b.createdAt || '');
    if (byCreated) return byCreated;
    return a.id.localeCompare(b.id);
  });

export const goalProgress = (goal) => {
  const items = liveChecklist(goal);
  const done = items.filter((item) => item.done).length;
  return {
    done,
    total: items.length,
    complete: items.length > 0 && done === items.length
  };
};

export const goalProgressLabel = (goal) => {
  const { done, total } = goalProgress(goal);
  if (total === 0) return 'Add a step';
  return `${done}/${total}`;
};

export const isGoalComplete = (goal) => goalProgress(goal).complete;

export const listGoals = (map) => Object.values(normalizeGoals(map))
  .filter((goal) => !goal.deleted)
  .sort((a, b) => {
    const byDone = Number(isGoalComplete(a)) - Number(isGoalComplete(b));
    if (byDone) return byDone;
    const ad = a.targetDate || '9999-99-99';
    const bd = b.targetDate || '9999-99-99';
    if (ad !== bd) return ad.localeCompare(bd);
    return stampOf(b).localeCompare(stampOf(a));
  });

export const filterGoals = (goals, { query = '', status = 'all' } = {}) => {
  const q = String(query || '').trim().toLowerCase();
  return goals.filter((goal) => {
    const complete = isGoalComplete(goal);
    if (status === 'open' && complete) return false;
    if (status === 'done' && !complete) return false;
    if (!q) return true;
    const haystack = [goal.title, goal.notes, ...liveChecklist(goal).map((item) => item.text)]
      .join('\n')
      .toLowerCase();
    return haystack.includes(q);
  });
};

export const formatGoalDate = (isoDate) => {
  if (!DATE_RE.test(isoDate || '')) return '';
  const [year, month, day] = isoDate.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
};
